// src/pages/client/CookiePolicyPage.jsx
import React, { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  ArrowLeft,
  Cookie,
  Shield,
  Clock,
  RefreshCw,
  Globe,
  Database,
  Users,
  CheckCircle,
  AlertCircle,
  Info,
  Mail,
  Phone,
  MapPin,
  ArrowRight,
  Loader
} from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import useTranslation from '../../hooks/useTranslation';
import styles from '../../styles/CookiePolicy.module.css';
import colors from '../../constants/colors';
import {
  getPublicSettings,
  selectPublicSettings,
  selectSettingsLoading
} from '../../store/slices/platformSettingsSlice';

const CookiePolicyPage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { currentLanguage } = useLanguage();

  // ======================================================
  // REDUX STATE
  // ======================================================
  const publicSettings = useSelector(selectPublicSettings);
  const loading = useSelector(selectSettingsLoading);

  // ======================================================
  // EFFECTS
  // ======================================================
  
  useEffect(() => {
    if (!publicSettings) {
      dispatch(getPublicSettings());
    }
  }, [dispatch, publicSettings]);

  // ======================================================
  // TRANSLATIONS
  // ======================================================
  const { translatedText: cookiePolicy } = useTranslation('Cookie Policy');
  const { translatedText: lastUpdated } = useTranslation('Last Updated');
  const { translatedText: cookiePolicyIntro } = useTranslation('This Cookie Policy explains how Haitian Services Platform uses cookies and similar technologies to recognize you when you visit our website. It explains what these technologies are and why we use them, as well as your rights to control our use of them.');
  const { translatedText: whatAreCookies } = useTranslation('What Are Cookies?');
  const { translatedText: whatAreCookiesDesc } = useTranslation('Cookies are small data files that are placed on your computer or mobile device when you visit a website. Cookies are widely used by website owners in order to make their websites work, or to work more efficiently, as well as to provide reporting information.');
  const { translatedText: howWeUseCookies } = useTranslation('How We Use Cookies');
  const { translatedText: howWeUseCookiesDesc } = useTranslation('We use cookies for a variety of reasons detailed below. Unfortunately, in most cases there are no industry standard options for disabling cookies without completely disabling the functionality and features they add to this site. It is recommended that you leave on all cookies if you are not sure whether you need them or not in case they are used to provide a service that you use.');
  const { translatedText: typesOfCookies } = useTranslation('Types of Cookies We Use');
  const { translatedText: essentialCookies } = useTranslation('Essential Cookies');
  const { translatedText: essentialCookiesDesc } = useTranslation('These cookies are strictly necessary to provide you with services available through our website and to use some of its features, such as access to secure areas.');
  const { translatedText: performanceCookies } = useTranslation('Performance Cookies');
  const { translatedText: performanceCookiesDesc } = useTranslation('These cookies collect information about how you use our website, such as which pages you visit most often. This helps us improve how our website works.');
  const { translatedText: functionalCookies } = useTranslation('Functional Cookies');
  const { translatedText: functionalCookiesDesc } = useTranslation('These cookies enable our website to provide enhanced functionality and personalization. They may be set by us or by third-party providers whose services we have added to our pages.');
  const { translatedText: targetingCookies } = useTranslation('Targeting Cookies');
  const { translatedText: targetingCookiesDesc } = useTranslation('These cookies may be set through our site by our advertising partners. They may be used by those companies to build a profile of your interests and show you relevant advertisements on other sites.');
  const { translatedText: thirdPartyCookies } = useTranslation('Third-Party Cookies');
  const { translatedText: thirdPartyCookiesDesc } = useTranslation('In some special cases, we also use cookies provided by trusted third parties. The following section details which third party cookies you might encounter through this site.');
  const { translatedText: cookieDuration } = useTranslation('Cookie Duration');
  const { translatedText: sessionCookies } = useTranslation('Session Cookies');
  const { translatedText: sessionCookiesDesc } = useTranslation('These cookies are temporary and are erased when you close your browser.');
  const { translatedText: persistentCookies } = useTranslation('Persistent Cookies');
  const { translatedText: persistentCookiesDesc } = useTranslation('These cookies remain on your device for a set period of time or until you delete them.');
  const { translatedText: controllingCookies } = useTranslation('Controlling Cookies');
  const { translatedText: controllingCookiesDesc } = useTranslation('You can control and manage cookies in various ways. Please keep in mind that removing or blocking cookies can negatively impact your user experience and parts of our website may no longer be fully accessible.');
  const { translatedText: browserSettings } = useTranslation('Browser Settings');
  const { translatedText: browserSettingsDesc } = useTranslation('Most browsers allow you to control cookies through their settings preferences. However, if you limit the ability of websites to set cookies, you may worsen your overall user experience.');
  const { translatedText: optOut } = useTranslation('Opt-Out Options');
  const { translatedText: optOutDesc } = useTranslation('You can opt out of certain types of cookies by adjusting your browser settings or using third-party opt-out tools.');
  const { translatedText: updatesToPolicy } = useTranslation('Updates to This Policy');
  const { translatedText: updatesToPolicyDesc } = useTranslation('We may update this Cookie Policy from time to time in order to reflect changes to the cookies we use or for other operational, legal or regulatory reasons. Please revisit this Cookie Policy regularly to stay informed about our use of cookies.');
  const { translatedText: contactUsTitle } = useTranslation('Contact Us');
  const { translatedText: contactUsDesc } = useTranslation('If you have any questions about our use of cookies, please contact us at:');
  const { translatedText: backToHome } = useTranslation('Back to Home');
  const { translatedText: homeTitle } = useTranslation('Haitian Services Platform');
  const { translatedText: connectingHaitians } = useTranslation('Connecting Haitians Worldwide, One Service at a Time');
  const { translatedText: madeWithLove } = useTranslation('All rights reserved');

  // ======================================================
  // FOOTER TRANSLATIONS
  // ======================================================
  const { translatedText: quickLinks } = useTranslation('Quick Links');
  const { translatedText: findProviders } = useTranslation('Find Providers');
  const { translatedText: supportUs } = useTranslation('Support Us');
  const { translatedText: helpAndSupport } = useTranslation('Help & Support');
  const { translatedText: aboutUs } = useTranslation('About Us');
  const { translatedText: forProviders } = useTranslation('For Providers');
  const { translatedText: becomeAProvider } = useTranslation('Become a Provider');
  const { translatedText: providerLogin } = useTranslation('Provider Login');
  const { translatedText: dashboard } = useTranslation('Dashboard');
  const { translatedText: settings } = useTranslation('Settings');
  const { translatedText: legal } = useTranslation('Legal');
  const { translatedText: termsOfService } = useTranslation('Terms of Service');
  const { translatedText: privacyPolicy } = useTranslation('Privacy Policy');

  // ======================================================
  // DATA
  // ======================================================
  
  // Get dynamic data from public settings
  const platformName = publicSettings?.platformInfo?.name || homeTitle;
  const contactEmail = publicSettings?.platformInfo?.contactEmail || 'privacy@haitianservices.com';
  const contactPhone = publicSettings?.platformInfo?.contactPhone || '+1 (800) 555-HAITI';
  const address = publicSettings?.platformInfo?.address || {};
  const addressString = address.street && address.city 
    ? `${address.street}, ${address.city}, ${address.country || 'Haiti'}`
    : 'Port-au-Prince, Haiti';

  // Social links from settings or fallback
  const socialLinks = publicSettings?.socialLinks || {
    facebook: 'https://facebook.com/haitianservices',
    instagram: 'https://instagram.com/haitianservices',
    twitter: 'https://twitter.com/haitianservices',
    youtube: 'https://youtube.com/haitianservices'
  };

  // Cookie types data
  const cookieTypes = [
    {
      icon: Shield,
      title: essentialCookies,
      description: essentialCookiesDesc,
      color: colors.primary
    },
    {
      icon: RefreshCw,
      title: performanceCookies,
      description: performanceCookiesDesc,
      color: colors.secondary
    },
    {
      icon: Users,
      title: functionalCookies,
      description: functionalCookiesDesc,
      color: colors.accentGold
    },
    {
      icon: Globe,
      title: targetingCookies,
      description: targetingCookiesDesc,
      color: colors.success
    }
  ];

  // Cookie duration types
  const durationTypes = [
    {
      icon: Clock,
      title: sessionCookies,
      description: sessionCookiesDesc,
      color: colors.secondary
    },
    {
      icon: Database,
      title: persistentCookies,
      description: persistentCookiesDesc,
      color: colors.primary
    }
  ];

  // ======================================================
  // RENDER
  // ======================================================
  
  if (loading && !publicSettings) {
    return (
      <div className={styles.loadingContainer}>
        <Loader size={48} className={styles.spinner} />
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <div className={styles.cookieContainer}>
      {/* Header */}
      <div className={styles.cookieHeader}>
        <button 
          className={styles.backButton}
          onClick={() => navigate(-1)}
        >
          <ArrowLeft size={24} color="#1A1814" />
        </button>
        <h2>{cookiePolicy}</h2>
      </div>

      <div className={styles.container}>
        {/* Hero Section */}
        <div className={styles.heroSection}>
          <div className={styles.heroIconWrapper}>
            <Cookie size={48} color="#FFFFFF" />
          </div>
          <h1 className={styles.heroTitle}>{cookiePolicy}</h1>
          <p className={styles.heroSubtitle}>
            {lastUpdated}: {new Date().toLocaleDateString()}
          </p>
        </div>

        {/* Introduction */}
        <div className={styles.introSection}>
          <p className={styles.introText}>{cookiePolicyIntro}</p>
        </div>

        {/* What Are Cookies */}
        <div className={styles.section}>
          <div className={styles.sectionIconWrapper}>
            <Info size={24} color={colors.primary} />
          </div>
          <h2 className={styles.sectionTitle}>{whatAreCookies}</h2>
          <p className={styles.sectionText}>{whatAreCookiesDesc}</p>
        </div>

        {/* How We Use Cookies */}
        <div className={styles.section}>
          <div className={styles.sectionIconWrapper}>
            <AlertCircle size={24} color={colors.secondary} />
          </div>
          <h2 className={styles.sectionTitle}>{howWeUseCookies}</h2>
          <p className={styles.sectionText}>{howWeUseCookiesDesc}</p>
        </div>

        {/* Types of Cookies */}
        <div className={styles.section}>
          <div className={styles.sectionIconWrapper}>
            <CheckCircle size={24} color={colors.success} />
          </div>
          <h2 className={styles.sectionTitle}>{typesOfCookies}</h2>
          <div className={styles.cookieTypesGrid}>
            {cookieTypes.map((cookie, index) => {
              const Icon = cookie.icon;
              return (
                <div key={index} className={styles.cookieTypeCard}>
                  <div className={styles.cookieTypeIcon} style={{ backgroundColor: cookie.color + '15' }}>
                    <Icon size={28} color={cookie.color} />
                  </div>
                  <h3 className={styles.cookieTypeTitle}>{cookie.title}</h3>
                  <p className={styles.cookieTypeDescription}>{cookie.description}</p>
                </div>
              );
            })}
          </div>
        </div>

        {/* Third Party Cookies */}
        <div className={styles.section}>
          <div className={styles.sectionIconWrapper}>
            <Globe size={24} color={colors.accentGold} />
          </div>
          <h2 className={styles.sectionTitle}>{thirdPartyCookies}</h2>
          <p className={styles.sectionText}>{thirdPartyCookiesDesc}</p>
        </div>

        {/* Cookie Duration */}
        <div className={styles.section}>
          <div className={styles.sectionIconWrapper}>
            <Clock size={24} color={colors.secondary} />
          </div>
          <h2 className={styles.sectionTitle}>{cookieDuration}</h2>
          <div className={styles.durationGrid}>
            {durationTypes.map((duration, index) => {
              const Icon = duration.icon;
              return (
                <div key={index} className={styles.durationCard}>
                  <div className={styles.durationIconWrapper} style={{ backgroundColor: duration.color + '15' }}>
                    <Icon size={24} color={duration.color} />
                  </div>
                  <h3 className={styles.durationTitle}>{duration.title}</h3>
                  <p className={styles.durationDescription}>{duration.description}</p>
                </div>
              );
            })}
          </div>
        </div>

        {/* Controlling Cookies */}
        <div className={styles.section}>
          <div className={styles.sectionIconWrapper}>
            <Shield size={24} color={colors.primary} />
          </div>
          <h2 className={styles.sectionTitle}>{controllingCookies}</h2>
          <p className={styles.sectionText}>{controllingCookiesDesc}</p>
          
          <div className={styles.controlOptions}>
            <div className={styles.controlOption}>
              <h4 className={styles.controlTitle}>{browserSettings}</h4>
              <p className={styles.controlText}>{browserSettingsDesc}</p>
            </div>
            <div className={styles.controlOption}>
              <h4 className={styles.controlTitle}>{optOut}</h4>
              <p className={styles.controlText}>{optOutDesc}</p>
            </div>
          </div>
        </div>

        {/* Updates */}
        <div className={styles.section}>
          <div className={styles.sectionIconWrapper}>
            <RefreshCw size={24} color={colors.accentGold} />
          </div>
          <h2 className={styles.sectionTitle}>{updatesToPolicy}</h2>
          <p className={styles.sectionText}>{updatesToPolicyDesc}</p>
        </div>

        {/* Contact Section */}
        <div className={styles.contactSection}>
          <h2 className={styles.contactTitle}>{contactUsTitle}</h2>
          <p className={styles.contactText}>{contactUsDesc}</p>
          <div className={styles.contactDetails}>
            <div className={styles.contactItem}>
              <Mail size={18} color={colors.primary} />
              <a href={`mailto:${contactEmail}`}>{contactEmail}</a>
            </div>
            <div className={styles.contactItem}>
              <Phone size={18} color={colors.primary} />
              <a href={`tel:${contactPhone.replace(/[^0-9+]/g, '')}`}>{contactPhone}</a>
            </div>
            <div className={styles.contactItem}>
              <MapPin size={18} color={colors.primary} />
              <span>{addressString}</span>
            </div>
          </div>
        </div>

        {/* Back to Home Button */}
        <Link to="/" className={styles.backToHomeBtn}>
          {backToHome}
          <ArrowRight size={18} />
        </Link>
      </div>

      {/* Footer */}
      <footer className={styles.footerFull}>
        <div className={styles.footerContent}>
          <div className={styles.footerGridFull}>
            {/* Brand Column */}
            <div className={styles.footerBrandColumn}>
              <div className={styles.footerBrandContainer}>
                <div className={styles.footerLogoWrapper}>
                  <img 
                    src="/logo/logo_hsm.png" 
                    alt="Haitian Services"
                    className={styles.footerLogo}
                  />
                </div>
                <div>
                  <div className={styles.footerBrandName}>{platformName}</div>
                  <div className={styles.footerBrandDescription}>{connectingHaitians}</div>
                </div>
              </div>
              <div className={styles.footerSocialLinks}>
                {socialLinks.facebook && (
                  <a href={socialLinks.facebook} target="_blank" rel="noopener noreferrer" className={styles.footerSocialIcon}>
                    <img src="https://s.magecdn.com/social/tc-facebook.svg" alt="Facebook" width="20" height="20" />
                  </a>
                )}
                {socialLinks.instagram && (
                  <a href={socialLinks.instagram} target="_blank" rel="noopener noreferrer" className={styles.footerSocialIcon}>
                    <img src="https://s.magecdn.com/social/tc-instagram.svg" alt="Instagram" width="20" height="20" />
                  </a>
                )}
                {socialLinks.twitter && (
                  <a href={socialLinks.twitter} target="_blank" rel="noopener noreferrer" className={styles.footerSocialIcon}>
                    <img src="https://s.magecdn.com/social/tc-twitter.svg" alt="Twitter" width="20" height="20" />
                  </a>
                )}
                {socialLinks.youtube && (
                  <a href={socialLinks.youtube} target="_blank" rel="noopener noreferrer" className={styles.footerSocialIcon}>
                    <img src="https://s.magecdn.com/social/tc-youtube.svg" alt="YouTube" width="20" height="20" />
                  </a>
                )}
              </div>
            </div>

            {/* Quick Links */}
            <div className={styles.footerLinksColumn}>
              <h3 className={styles.footerHeading}>{quickLinks}</h3>
              <ul className={styles.footerLinks}>
                <li><a href="/providers">{findProviders}</a></li>
                <li><a href="/donation">{supportUs}</a></li>
                <li><a href="/help-support">{helpAndSupport}</a></li>
                <li><a href="/about">{aboutUs}</a></li>
              </ul>
            </div>

            {/* For Providers */}
            <div className={styles.footerLinksColumn}>
              <h3 className={styles.footerHeading}>{forProviders}</h3>
              <ul className={styles.footerLinks}>
                <li><a href="/register">{becomeAProvider}</a></li>
                <li><a href="/login">{providerLogin}</a></li>
                <li><a href="/provider-dashboard">{dashboard}</a></li>
                <li><a href="/provider-settings">{settings}</a></li>
              </ul>
            </div>

            {/* Legal */}
            <div className={styles.footerLinksColumn}>
              <h3 className={styles.footerHeading}>{legal}</h3>
              <ul className={styles.footerLinks}>
                <li><a href="/terms">{termsOfService}</a></li>
                <li><a href="/privacy">{privacyPolicy}</a></li>
                <li><a href="/cookies">{cookiePolicy}</a></li>
              </ul>
            </div>
          </div>

          {/* Footer Bottom */}
          <div className={styles.footerDividerFull}></div>
          <div className={styles.footerBottomFull}>
            <div className={styles.footerFlag}>
              <div className={styles.flagStripes}>
                <div className={`${styles.stripe} ${styles.stripeBlue}`}></div>
                <div className={`${styles.stripe} ${styles.stripeRed}`}></div>
              </div>
            </div>
            <div className={styles.footerBottomText}>
              © {new Date().getFullYear()} {platformName}, {madeWithLove}
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default CookiePolicyPage;